import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Header from '../header/Header';
import Sidebar from '../sidebar/Sidebar';
import LoadingSpinner from '@/components/common/Loading/LoadingSpinner';
import { useLoading } from '@/contexts/loadingManager';

const Layout = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const { isLoading } = useLoading();

    // 모바일 화면 감지
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 992;
            setIsMobile(mobile);
            if (mobile) {
                setIsSidebarOpen(false);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // data-toggled 값 제어
    useEffect(() => {
        const htmlElement = document.documentElement;

        if (isMobile && isSidebarOpen) {
            // 모바일에서 사이드바가 열려있을 때 "open"
            htmlElement.setAttribute('data-toggled', 'open');
        } else {
            // 그 외의 경우 "close"
            htmlElement.setAttribute('data-toggled', 'close');
        }
    }, [isMobile, isSidebarOpen]);

    // 사이드바 토글
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // 모바일에서 사이드바 외부 클릭 시 닫기
    const handleOverlayClick = () => {
        if (isMobile && isSidebarOpen) {
            setIsSidebarOpen(false);
        }
    };

    return (
        <div
            className={`page ${isSidebarOpen ? 'sidebar-mini' : 'sidebar-closed'}`}
        >
            {/* 글로벌 로딩 스피너 */}
            {isLoading && <LoadingSpinner fullScreen message="처리 중..." />}

            {/* 헤더 */}
            <Header
                onMenuToggle={toggleSidebar}
                isSidebarOpen={isSidebarOpen}
            />

            {/* 사이드바 */}
            <Sidebar
                isOpen={isSidebarOpen}
                onMenuClick={() => {
                    if (isMobile) {
                        setIsSidebarOpen(false);
                    }
                }}
            />

            {/* 모바일 오버레이 */}
            {isMobile && isSidebarOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={handleOverlayClick}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 100,
                    }}
                />
            )}

            {/* 메인 콘텐츠 */}
            <div className="main-content app-content ">
                <div className="container-fluid">
                    {/* 페이지 콘텐츠 */}
                    <main className="main-container container-fluid">
                        <Outlet />
                    </main>
                </div>
            </div>

            {/* 맨 위로 가기 버튼 */}
            <div className="scrollToTop">
                <span className="arrow">
                    <i className="fe fe-arrow-up"></i>
                </span>
            </div>

            {/* 검색 모달 */}
            <div
                className="modal fade"
                id="searchModal"
                tabIndex={-1}
                aria-labelledby="searchModal"
                aria-hidden="true"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-body">
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control border-end-0 border-rounded"
                                    placeholder="Search Input"
                                    aria-label="Search Input"
                                    aria-describedby="button-addon2"
                                />
                                <button
                                    className="btn btn-primary"
                                    type="button"
                                    id="button-addon2"
                                >
                                    <i className="fe fe-search"></i>
                                </button>
                            </div>
                            <div className="mt-4">
                                <p className="font-weight-semibold text-muted mb-2">
                                    Recent Searches
                                </p>
                                <div className="ps-0">
                                    <a href="#" className="search-tags">
                                        <i className="fe fe-search me-2"></i>
                                        People
                                    </a>
                                    <a href="#" className="search-tags">
                                        <i className="fe fe-search me-2"></i>
                                        Pages
                                    </a>
                                    <a href="#" className="search-tags">
                                        <i className="fe fe-search me-2"></i>
                                        Articles
                                    </a>
                                    <a href="#" className="search-tags">
                                        <i className="fe fe-search me-2"></i>
                                        Tags
                                    </a>
                                </div>
                            </div>
                            <div className="mt-4">
                                <p className="font-weight-semibold text-muted mb-2">
                                    Apps and pages
                                </p>
                                <ul className="ps-0">
                                    <li className="list-group-item d-flex align-items-center">
                                        <a href="calendar.html">
                                            <span className="avatar avatar-sm avatar-rounded me-2">
                                                <img
                                                    src="/images/apps/calendar.png"
                                                    alt="img"
                                                />
                                            </span>
                                            Calendar
                                        </a>
                                    </li>
                                    <li className="list-group-item d-flex align-items-center">
                                        <a href="mail.html">
                                            <span className="avatar avatar-sm avatar-rounded me-2">
                                                <img
                                                    src="/images/apps/mail.png"
                                                    alt="img"
                                                />
                                            </span>
                                            Mail
                                        </a>
                                    </li>
                                    <li className="list-group-item d-flex align-items-center">
                                        <a href="buttons.html">
                                            <span className="avatar avatar-sm avatar-rounded me-2">
                                                <img
                                                    src="/images/apps/buttons.png"
                                                    alt="img"
                                                />
                                            </span>
                                            Buttons
                                        </a>
                                    </li>
                                </ul>
                            </div>
                            <div className="mt-4">
                                <p className="font-weight-semibold text-muted mb-2">
                                    Links
                                </p>
                                <ul className="ps-0">
                                    <li className="list-group-item d-flex align-items-center">
                                        <a href="#">
                                            <span className="avatar avatar-sm avatar-rounded me-2">
                                                <i className="fe fe-external-link"></i>
                                            </span>
                                            Documentation
                                        </a>
                                    </li>
                                    <li className="list-group-item d-flex align-items-center">
                                        <a href="#">
                                            <span className="avatar avatar-sm avatar-rounded me-2">
                                                <i className="fe fe-help-circle"></i>
                                            </span>
                                            Support
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <div className="btn-group ms-auto">
                                <button className="btn btn-sm btn-primary-light">
                                    Search
                                </button>
                                <button className="btn btn-sm btn-primary">
                                    Clear Recents
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Layout;
